import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../../hooks/useApi.js";

function deepClone(value) {
  if (value === null || value === undefined) return value;
  return JSON.parse(JSON.stringify(value));
}

/**
 * Manages a single KB resource: local edits, dirty tracking, save, discard, reload.
 *
 * @param {string} resourceName  - Key used in PUT /kb/:resourceName
 * @param {*}      initialData   - Data provided by the parent (from the global GET /kb load)
 * @param {number} loadKey       - Increment in the parent to reset local state after a global reload
 */
export function useCatalogResource(resourceName, initialData, loadKey = 0) {
  const api = useApi();
  const [serverData, setServerData] = useState(() => deepClone(initialData));
  const [localData, setLocalData] = useState(() => deepClone(initialData));
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState("");

  // When the parent does a full catalog reload (loadKey bumps), reset to new initialData
  useEffect(() => {
    setServerData(deepClone(initialData));
    setLocalData(deepClone(initialData));
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  const dirty = useMemo(
    () => JSON.stringify(localData) !== JSON.stringify(serverData),
    [localData, serverData]
  );

  const update = useCallback((updater) => {
    setLocalData((prev) => updater(deepClone(prev)));
  }, []);

  const discard = useCallback(() => {
    setLocalData(deepClone(serverData));
    setError("");
  }, [serverData]);

  const save = useCallback(
    async (onSuccess) => {
      try {
        setSaving(true);
        setError("");
        const { data } = await api.put(`/kb/${resourceName}`, localData);
        const saved = data?.data ?? localData;
        setServerData(deepClone(saved));
        setLocalData(deepClone(saved));
        onSuccess?.();
        return true;
      } catch (err) {
        setError(err.response?.data?.message || "No se pudo guardar");
        return false;
      } finally {
        setSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, resourceName, localData]
  );

  const reload = useCallback(async () => {
    try {
      setReloading(true);
      setError("");
      const { data } = await api.get(`/kb/${resourceName}`);
      const fresh = data?.data ?? data;
      setServerData(deepClone(fresh));
      setLocalData(deepClone(fresh));
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo recargar");
    } finally {
      setReloading(false);
    }
  }, [api, resourceName]);

  return {
    data: localData,
    dirty,
    saving,
    loading: reloading,
    error,
    setError,
    update,
    save,
    discard,
    reload,
  };
}
